import LoadingFullScreen from "components/common/LoadingFullScreen";
import { useGetAllClustersQuery } from "generated/graphql";
import { LOADING } from "graphql/helpers/queryHelpers";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { find } from "lodash";
import { Navigate } from "react-router-dom";
import NoSubscriptions from "./components/NoSubscriptions";
import { useSubscriptions } from "./contexts/InQueueSubscriptionsContext";
import { useLastSelectedClusterSlug } from "./hooks/useLastClusterStorage";
import { Cluster, convertHasuraClusterToCluster } from "./types/clusterType";
import { InQueueSubscription } from "./types/projectType";
import { canUserViewCluster } from "./utils/clusterStatus";

const getClusterFromSubscription = (
    clusters: Cluster[],
    subscription: InQueueSubscription
) => {
    return find(
        clusters,
        (c) =>
            c.region === subscription.clusterInfo.region &&
            c.studyCycle === subscription.clusterInfo.studyCycle &&
            c.studyGroup === subscription.clusterInfo.studyGroup &&
            c.studyPhase === subscription.clusterInfo.studyPhase
    );
};

// Redirect to the last cluster the user had open, if known, or to the first
// cluster in the user's subscriptions. If the user has no subscriptions, show
// the "no subscriptions" page.
export const RedirectToCluster: React.FC = () => {
    const maybeLastSelectedClusterSlug = useLastSelectedClusterSlug();
    const subscriptions = useSubscriptions();
    const { data: allClustersData } = useGetAllClustersQuery();

    if (subscriptions === LOADING || !allClustersData) {
        return <LoadingFullScreen />;
    }
    const allClusters = convertHasuraClusterToCluster(allClustersData);

    // Get the first cluster from the user's subscriptions that they can view.
    // Future: Query for the cluster once we update schema.
    // linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
    let firstCluster: Cluster | undefined;
    for (const subscription of subscriptions) {
        const cluster = getClusterFromSubscription(allClusters, subscription);
        if (!cluster) {
            continue;
        }
        if (canUserViewCluster(subscription, cluster)) {
            firstCluster = cluster;
            break;
        }
    }
    const lastCluster = allClusters.find(
        (cluster) =>
            cluster.slug === maybeLastSelectedClusterSlug &&
            subscriptions.some((subscription) =>
                canUserViewCluster(subscription, cluster)
            )
    );
    const url = lastCluster?.slug ?? firstCluster?.slug;
    if (!url) {
        return <NoSubscriptions />;
    }

    return <Navigate to={`/${IN_QUEUE_PATH}/${url}`} />;
};
